import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  QueryList,
  ViewChildren,
  computed,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subscription, switchMap, map } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FlyoutDataType, FlyoutOpeningData } from '../flyout-models';
import { FlyoutService } from '../flyout.service';
import {
  DataPlatformProfileRow,
  DataPlatformProfileSource,
  DataPlatformVisitRow,
  DataPlatformVisitStatusResponse,
  DataPlatformConsentEvent,
  DataPlatformAudienceDefinition,
  DataPlatformProfileIdentifier,
} from '../../data-platform/data-platform.model';
import { DataPlatformService } from '../../data-platform/data-platform.service';
import { hasVisitStatusChanged, isOpenVisit, parseUtc, pollVisitStatus } from '../../data-platform/visit-status.utils';
import { formatVisitDuration } from '../../framework/device-utils';
import {
  getDimensionTypeLabel,
  getEngagementStars,
  getTier,
  getTierIcon,
  getInsightPresets,
  InsightPreset,
  UrlInterestProfileMap,
  buildDimensionTypeDonutFromVisits,
  translateDimensionTypeValue,
  DimensionTypeDonut,
  identitySourceBadge,
  IdentitySourceBadge,
} from '../../data-platform/data-platform.utils';
import { audienceEvaluateRules } from '../../data-platform/audiences/audience-filter-to-dp-rule.utils';
import { CurrentContextService } from '../../core/current-context.service';
import { Currency } from '../../shared/account/Currency';
import { DateService } from '../../shared/services/date-service';
import { FormatDate } from '../../shared/pipes/format-date.pipe';
import { SettingsService } from '../../shared/services/admin-settings.service';
import { ConfirmationDialogService } from 'app/shared/confirmation-dialog/confirmation-dialog.service';
import { SnackBarService } from 'app/core/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { PropertyLabelService } from '../../shared/services/property-label.service';
import { UrlInterestProfilesService } from '../../data-platform/url-interest-profiles.service';
import { AudienceService } from '../../audience/audience.service';
import { DestroyRef } from '@angular/core';
import { PurchaseListItem } from '../data-platform-purchases-list/data-platform-purchases-list.component';
import { MatDialog } from '@angular/material/dialog';
import {
  ProfileUnmergeDialogComponent,
  ProfileUnmergeDialogData,
} from '../profile-unmerge-dialog/profile-unmerge-dialog.component';

/** Epoch floor so the flyout's visit queries cover the whole (all-time) profile. */
const ALL_TIME_FROM = new Date(0).toISOString();

const MS_PER_DAY = 24 * 60 * 60 * 1000;
/** Recency bucket thresholds (days since last seen). */
const RECENCY_WEEK_DAYS = 7;
const RECENCY_MONTH_DAYS = 30;
/** Number of recent visits plotted in the engagement trend sparkline. */
const SCORE_TREND_VISITS = 5;
/** Server prefix on page-visit goals; stripped for display (same rule as the visit events timeline). */
const PAGE_GOAL_PREFIX = 'visitedpage-';

export interface VisitSummary {
  sessionId: number;
  startedAtDate: Date;
  pageCount: number;
  goalCount: number;
  revenue: number;
  duration: string;
  tier: 'hot' | 'active' | 'warming' | 'cold';
  tierTooltip: string;
  visitRow: DataPlatformVisitRow;
}

export interface VisitRow extends VisitSummary {
  hasPurchaseWithoutRevenue: boolean;
  isOngoing: boolean;
}

type RecencyBucket = 'week' | 'month' | 'inactive';

@Component({
  selector: 'app-data-platform-profile-flyout',
  templateUrl: './data-platform-profile-flyout.component.html',
  styleUrls: ['./data-platform-profile-flyout.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DataPlatformProfileFlyoutComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() flyoutOpeningData: FlyoutOpeningData;

  @ViewChildren('flyoutHeaderEl', { read: ElementRef })
  private headerEls: QueryList<ElementRef<HTMLElement>>;

  protected profile: DataPlatformProfileRow;
  protected engagementStarIcons: string[] = [];
  protected tier: 'hot' | 'active' | 'warming' | 'cold' = 'cold';
  protected tierIcon = 'ac_unit';
  protected tierLabelKey = 'data-platform.tier-cold';
  protected firstSeenDate: Date;
  protected lastSeenDate: Date;
  /** Header: the contact's name when known, else the latest identifier, else the UID. */
  protected headerTitle = '';
  /** Header secondary line: the identifier + email not already shown as the title, deduped. */
  protected headerSubtitle = '';
  /** Email driving the header avatar; empty renders the anonymous icon. */
  protected avatarEmail = '';
  /** Started-at of the visit to navigate back to, when this profile was opened from a visit flyout. */
  protected backVisitDate: Date | null = null;
  private readonly rawTraits = signal<{ key: string; value: string }[]>([]);
  /** Property key to widget input-field label, e.g. first_name to the label the form showed. */
  private readonly propertyLabels = toSignal(inject(PropertyLabelService).getLabelMap(), {
    initialValue: new Map<string, string>(),
  });
  protected readonly traitsEntries = computed(() =>
    this.rawTraits().map((trait) => {
      const label = this.propertyLabels().get(trait.key) ?? trait.key;
      // Widget input labels often carry their own punctuation ("Choose here:", "Vem?")
      return {
        ...trait,
        label: /[:?!]\s*$/.test(label) ? label : `${label}:`,
        // A value with no space cannot wrap, so the row truncates it instead of overflowing the card.
        isIdentifierValue: !/\s/.test(trait.value ?? ''),
      };
    }),
  );
  protected deviceViews: { uid: number; lastSeenDate: Date; browser: string; os: string }[] = [];
  private readonly rawIdentifiers = signal<DataPlatformProfileIdentifier[]>([]);
  protected readonly identifierRows = computed(() => {
    const rows = this.rawIdentifiers().map((id) => {
      const badge = this.visibleBadge(id.source);
      const firstSeen = id.firstSeen ? this.dateService.utcToLocalTime(id.firstSeen) : null;
      return { value: id.value, type: id.type, badge, sourceTooltip: this.sourceTooltip(badge, firstSeen) };
    });
    // The profile's most recently seen identifier leads the list.
    const latest = this.profile?.latestIdentifier;
    if (!latest) {
      return rows;
    }
    return [...rows.filter((row) => row.value === latest), ...rows.filter((row) => row.value !== latest)];
  });
  protected source: DataPlatformProfileSource | null = null;
  protected consentEvents: DataPlatformConsentEvent[] = [];
  protected topDimensionDisplay = '';
  protected currency: Currency | undefined;
  /** False when the profile was derived from visits (visit→profile entry): the server-computed
   *  profile engagement score isn't available per-uid, so the score card is hidden. */
  protected profileScoreAvailable = true;

  protected isDeleting = false;

  // Activity card (recency + tenure), derived from lastSeen/firstSeen in applyProfile.
  protected recencyBucket: RecencyBucket = 'week';
  protected recencyBadgeKey = '';
  /** Position of the "you are here" dot within the active bucket segment, in percent. */
  protected recencyDotLeft = 50;
  protected tenureKey = '';
  protected tenureCount = 0;

  /** True while the flyout header is scrolled out of view; shows the condensed sticky header. */
  protected readonly showMiniHeader = signal(false);
  private headerObserver: IntersectionObserver | null = null;

  /** True while fetching the profile row by uid (opened from a visit rather than the profiles list). */
  protected readonly isLoadingProfile = signal(false);
  protected readonly visitSummaries = signal<VisitSummary[]>([]);
  protected readonly isLoadingVisits = signal(false);
  private readonly latestVisitStatus = signal<DataPlatformVisitStatusResponse | null>(null);
  private readonly polledSessionId = signal<number | null>(null);
  protected readonly hasOngoingVisit = computed(() => isOpenVisit(this.latestVisitStatus()));
  protected readonly purchaseVisits = signal<VisitSummary[]>([]);
  protected readonly isLoadingPurchases = signal(false);
  protected readonly allTimeTotalRevenue = computed(() =>
    this.purchaseVisits().reduce((sum, s) => sum + (s.visitRow.totalRevenue ?? 0), 0),
  );
  protected readonly purchaseItems = computed<PurchaseListItem[]>(() =>
    this.purchaseVisits().map((p) => ({
      key: p.sessionId,
      date: p.startedAtDate,
      amount: p.visitRow.totalRevenue ?? 0,
    })),
  );
  /** Index 0 of metricsGoalNames is the purchase goal, which the backend stamps on purchase events. */
  private readonly purchaseGoalName = signal('');
  protected readonly visitRows = computed<VisitRow[]>(() => {
    const purchaseGoal = this.purchaseGoalName();
    return this.visitSummaries().map((summary) => ({
      ...summary,
      hasPurchaseWithoutRevenue:
        summary.revenue <= 0 && !!purchaseGoal && (summary.visitRow.goalNames ?? []).includes(purchaseGoal),
      isOngoing: this.hasOngoingVisit() && summary.sessionId === this.polledSessionId(),
    }));
  });
  protected readonly matchedInsights = signal<InsightPreset[]>([]);
  protected readonly urlInterestProfileMap = signal<UrlInterestProfileMap>(new Map());

  protected readonly dimensionTypeDonut = computed<DimensionTypeDonut[]>(() => {
    const map = this.urlInterestProfileMap();
    const perVisitComposites = this.visitSummaries().map((s) => s.visitRow.topDimensions ?? []);
    return buildDimensionTypeDonutFromVisits(perVisitComposites, map, this.translateService);
  });

  protected readonly profileGoalItems = computed<{ name: string; count: number; icon: string }[]>(() => {
    const counts = new Map<string, number>();
    for (const summary of this.visitSummaries()) {
      for (const name of summary.visitRow.goalNames ?? []) {
        counts.set(name, (counts.get(name) ?? 0) + 1);
      }
    }
    return Array.from(counts.entries())
      .map(([rawName, count]) => ({
        name: rawName.startsWith(PAGE_GOAL_PREFIX) ? rawName.slice(PAGE_GOAL_PREFIX.length) : rawName,
        count,
        icon: rawName.startsWith(PAGE_GOAL_PREFIX) ? 'bookmark' : 'flag',
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  });

  /** Engagement trend across the most recent visits, plotted oldest→newest (SVG viewBox 0 0 110 26). */
  protected readonly scoreTrend = computed<{ points: string; endX: number; endY: number; count: number } | null>(() => {
    const recent = this.visitSummaries()
      .slice(0, SCORE_TREND_VISITS)
      .reverse()
      .map((s) => s.visitRow.engagementScore ?? 0);
    if (recent.length < 2) return null;
    const coords = recent.map((score, i) => ({
      x: Math.round((4 + (i / (recent.length - 1)) * 102) * 10) / 10,
      y: Math.round((23 - (Math.max(0, Math.min(100, score)) / 100) * 20) * 10) / 10,
    }));
    return {
      points: coords.map((c) => `${c.x},${c.y}`).join(' '),
      endX: coords[coords.length - 1].x,
      endY: coords[coords.length - 1].y,
      count: recent.length,
    };
  });

  // Flatten coupons across the profile's visits and dedup by (couponSetId, code).
  // IsCopied is OR'd across visits — once copied anywhere, it stays copied on the profile view.
  // A legacy copy arrives without a code, so each one is kept.
  protected readonly distributedCoupons = computed(() => {
    const byKey = new Map<string, { couponSetId: string | null; code: string; isCopied: boolean }>();
    for (const [visitIndex, summary] of this.visitSummaries().entries()) {
      for (const [couponIndex, coupon] of (summary.visitRow.distributedCoupons ?? []).entries()) {
        const key = coupon.code ? `${coupon.couponSetId ?? ''}|${coupon.code}` : `no-code|${visitIndex}|${couponIndex}`;
        const existing = byKey.get(key);
        if (existing) {
          existing.isCopied = existing.isCopied || coupon.isCopied;
        } else {
          byKey.set(key, { ...coupon });
        }
      }
    }
    return Array.from(byKey.values());
  });

  private readonly flyoutService = inject(FlyoutService);
  private readonly dataPlatformService = inject(DataPlatformService);
  private readonly currentContextService = inject(CurrentContextService);
  private readonly dateService = inject(DateService);
  private readonly translateService = inject(TranslateService);
  private readonly formatDate = inject(FormatDate);
  private readonly urlInterestProfilesService = inject(UrlInterestProfilesService);
  private readonly audienceService = inject(AudienceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly settingsService = inject(SettingsService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly confirmationDialogService = inject(ConfirmationDialogService);

  private readonly dialog = inject(MatDialog);

  private readonly adminToggle = toSignal(
    this.settingsService.settings$.pipe(map((settings) => settings.adminToggle)),
    { initialValue: false },
  );

  protected readonly canUnmerge = computed(() => this.adminToggle() && this.rawIdentifiers().length >= 2);

  /** The source badge for an identifier, hidden when it is admin-only and the viewer is not an admin. */
  private visibleBadge(source: string): IdentitySourceBadge | null {
    const badge = identitySourceBadge(source);
    return badge && (!badge.adminOnly || this.adminToggle()) ? badge : null;
  }

  private sourceTooltip(badge: IdentitySourceBadge | null, firstSeen: Date | null): string {
    const trigger = badge?.triggerKey ? this.translateService.instant(badge.triggerKey) : null;
    const seen = firstSeen
      ? this.translateService.instant('data-platform.identifier-first-seen', {
          date: this.formatDate.transform(firstSeen),
        })
      : null;
    return [trigger, seen].filter(Boolean).join(' · ');
  }

  private profileSubscription: Subscription;
  private visitsSubscription: Subscription;
  private purchasesSubscription: Subscription;
  private traitsSubscription: Subscription;
  private consentSubscription: Subscription;
  private insightsSubscription: Subscription;
  private visitStatusSubscription: Subscription;

  ngOnChanges() {
    const ctx = this.currentContextService.currentContextBS.getValue();
    this.currency = ctx?.account?.currency as Currency;
    this.purchaseGoalName.set(ctx?.account?.metricsGoalNames?.[0] ?? '');

    const backVisit = this.flyoutOpeningData?.dataPlatformBackVisitRow;
    this.backVisitDate = backVisit ? this.dateService.utcToLocalTime(backVisit.startedAt) : null;

    const row = this.flyoutOpeningData?.dataPlatformProfileRow;
    if (row) {
      // Opened from the profiles list — the row (incl. engagement score) is already in hand.
      this.profile = row;
      this.profileScoreAvailable = true;
      this.applyProfile();
    } else if (this.flyoutOpeningData?.uid != null) {
      // Opened from a visit — only the uid is known; fetch the profile row first.
      this.fetchProfile(this.flyoutOpeningData.uid);
    }

    this.loadUrlInterestProfileMap();
  }

  ngAfterViewInit() {
    this.observeHeader();
    this.headerEls.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.observeHeader());
  }

  /** Toggles the condensed sticky header based on whether the full header is scrolled out of view. */
  private observeHeader() {
    this.headerObserver?.disconnect();
    this.headerObserver = null;
    const el = this.headerEls?.first?.nativeElement;
    if (!el) {
      this.showMiniHeader.set(false);
      return;
    }
    this.headerObserver = new IntersectionObserver(([entry]) => this.showMiniHeader.set(!entry.isIntersecting));
    this.headerObserver.observe(el);
  }

  /** Initializes the flyout from the current profile row and kicks off the lazy section loads. */
  private applyProfile() {
    if (!this.profile) return;
    this.stopLatestVisitPolling();
    if (this.profileScoreAvailable) {
      this.engagementStarIcons = getEngagementStars(this.profile.engagementScore);
      this.tier = getTier(this.profile.engagementScore);
      this.tierIcon = getTierIcon(this.tier);
      this.tierLabelKey = `data-platform.tier-${this.tier}`;
    } else {
      this.engagementStarIcons = [];
    }
    this.firstSeenDate = this.dateService.utcToLocalTime(this.profile.firstSeen);
    this.lastSeenDate = this.dateService.utcToLocalTime(this.profile.lastSeen);
    this.applyActivity();
    this.headerTitle = this.profile.name?.trim() || this.profile.latestIdentifier || `UID ${this.profile.uid}`;
    const headerSubs = [this.profile.latestIdentifier, this.profile.email].filter(
      (v): v is string => !!v && v !== this.headerTitle,
    );
    this.headerSubtitle = [...new Set(headerSubs)].join(' · ');
    this.avatarEmail =
      this.profile.email || (this.profile.latestIdentifier?.includes('@') ? this.profile.latestIdentifier : '');
    // Devices, identifier list, and source/campaign are not populated by the list query
    // (kept lean for list-load speed) — lazy-load them all in one roundtrip now that the
    // flyout is opening.
    this.deviceViews = [];
    this.rawIdentifiers.set([]);
    this.source = null;
    this.rawTraits.set([]);
    this.consentEvents = [];
    this.matchedInsights.set([]);
    this.topDimensionDisplay = this.formatTopDimension(this.profile.topDimension);
    this.loadProperties();
    this.loadConsentEvents();
    this.loadVisits();
    this.loadPurchases();
    this.loadInsights();
    this.loadDetails();
  }

  /** Derives the Activity card values (recency bucket + tenure) from first/last seen. */
  private applyActivity() {
    const now = Date.now();
    const daysSince = this.profile.lastSeen
      ? Math.max(0, Math.floor((now - this.lastSeenDate.getTime()) / MS_PER_DAY))
      : 0;

    if (daysSince <= RECENCY_WEEK_DAYS) {
      this.recencyBucket = 'week';
      this.recencyDotLeft = (daysSince / RECENCY_WEEK_DAYS) * 100;
    } else if (daysSince <= RECENCY_MONTH_DAYS) {
      this.recencyBucket = 'month';
      this.recencyDotLeft = ((daysSince - RECENCY_WEEK_DAYS) / (RECENCY_MONTH_DAYS - RECENCY_WEEK_DAYS)) * 100;
    } else {
      this.recencyBucket = 'inactive';
      this.recencyDotLeft = Math.min(1, (daysSince - RECENCY_MONTH_DAYS) / 60) * 100;
    }
    this.recencyDotLeft = Math.max(8, Math.min(92, this.recencyDotLeft));
    this.recencyBadgeKey = `data-platform.recency-${this.recencyBucket}`;

    const tenureDays = this.profile.firstSeen
      ? Math.max(0, Math.floor((now - this.firstSeenDate.getTime()) / MS_PER_DAY))
      : 0;
    if (tenureDays < 31) {
      this.tenureCount = Math.max(1, tenureDays);
      this.tenureKey = this.tenureCount === 1 ? 'data-platform.tenure-day' : 'data-platform.tenure-days';
    } else if (tenureDays < 365) {
      this.tenureCount = Math.round(tenureDays / 30.44);
      this.tenureKey = this.tenureCount === 1 ? 'data-platform.tenure-month' : 'data-platform.tenure-months';
    } else {
      this.tenureCount = Math.floor(tenureDays / 365.25);
      this.tenureKey = this.tenureCount === 1 ? 'data-platform.tenure-year' : 'data-platform.tenure-years';
    }
  }

  /**
   * Opened from a visit: the visit's uid is one device, so an identified visitor is resolved by
   * identifier to the canonical profile row. Anonymous ones keep the per-device row.
   */
  private fetchProfile(uid: number) {
    const siteIds = this.flyoutOpeningData?.dataPlatformFilter?.siteIds;
    if (!siteIds?.length) return;

    this.profileScoreAvailable = false;
    this.profileSubscription?.unsubscribe();
    this.isLoadingProfile.set(true);

    const identifier = this.flyoutOpeningData?.dataPlatformBackVisitRow?.identifier?.trim();
    if (!identifier) {
      this.deriveProfile(uid, siteIds);
      return;
    }

    this.profileSubscription = this.dataPlatformService
      .getProfiles(
        {
          filter: { siteIds, from: ALL_TIME_FROM, identifierFilter: identifier },
          pageSize: 1,
          includeTotalCount: false,
        },
        'profiles-flyout-resolve',
      )
      .subscribe({
        next: (result) => {
          const row = result?.isPositive() && result.data?.profiles?.length ? result.data.profiles[0] : null;
          if (!row) {
            this.deriveProfile(uid, siteIds);
            return;
          }
          this.isLoadingProfile.set(false);
          this.profileScoreAvailable = true;
          this.profile = row;
          this.applyProfile();
        },
        error: () => this.deriveProfile(uid, siteIds),
      });
  }

  /**
   * No identifier to resolve by, so look the profile up by uid. Its stored stats are all-time, while
   * deriving them from the visits list only sees what has not expired yet and stops at one page.
   */
  private deriveProfile(uid: number, siteIds: number[]) {
    this.profileSubscription = this.dataPlatformService
      .getProfiles(
        {
          filter: { siteIds, from: ALL_TIME_FROM, uid },
          pageSize: 1,
          includeTotalCount: false,
        },
        'profiles-flyout-uid',
      )
      .subscribe({
        next: (result) => {
          const row = result?.isPositive() && result.data?.profiles?.length ? result.data.profiles[0] : null;
          if (!row) {
            this.deriveProfileFromVisitList(uid, siteIds);
            return;
          }
          this.isLoadingProfile.set(false);
          this.profileScoreAvailable = true;
          this.profile = row;
          this.applyProfile();
        },
        error: () => this.deriveProfileFromVisitList(uid, siteIds),
      });
  }

  /** Last resort when the profile has no stored row yet: per-device stats, no engagement score. */
  private deriveProfileFromVisitList(uid: number, siteIds: number[]) {
    this.profileSubscription = this.dataPlatformService
      .getVisits(
        {
          filter: { siteIds, from: ALL_TIME_FROM },
          uid,
          includeEvents: false,
          includeTotalCount: false,
          pageSize: 200,
          sortDirection: 'desc',
        },
        'profiles-flyout-derive',
      )
      .subscribe({
        next: (result) => {
          this.isLoadingProfile.set(false);
          const visits = result?.isPositive() && result.data?.visits ? result.data.visits : [];
          this.profile = this.deriveProfileFromVisits(uid, visits);
          this.applyProfile();
        },
        error: () => this.isLoadingProfile.set(false),
      });
  }

  /** Aggregates a visitor's visits into a profile row (lifetime stats, latest device, top dimension). */
  private deriveProfileFromVisits(uid: number, visits: DataPlatformVisitRow[]): DataPlatformProfileRow {
    const base: DataPlatformProfileRow = {
      uid,
      firstSeen: '',
      lastSeen: '',
      totalEvents: 0,
      totalSessions: 0,
      totalRevenue: 0,
      lastBrowser: '',
      lastOs: '',
      lastGeoCountry: '',
      lastGeoCity: '',
      topDimension: undefined,
      engagementScore: 0,
      tags: [],
      latestIdentifier: undefined,
    };
    if (visits.length === 0) return base;

    // Requested desc by startedAt; sort defensively so [0] is the most recent visit.
    const sorted = [...visits].sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1));
    const latest = sorted[0];
    const firstSeen = sorted.reduce(
      (min, v) => (v.startedAt && v.startedAt < min ? v.startedAt : min),
      latest.startedAt,
    );
    const lastSeen = sorted.reduce((max, v) => {
      const ended = v.endedAt || v.startedAt;
      return ended && ended > max ? ended : max;
    }, latest.endedAt || latest.startedAt);

    return {
      ...base,
      firstSeen,
      lastSeen,
      totalEvents: visits.reduce((sum, v) => sum + (v.pageViewCount ?? 0) + (v.goalCount ?? 0), 0),
      totalSessions: visits.length,
      totalRevenue: visits.reduce((sum, v) => sum + (v.totalRevenue ?? 0), 0),
      lastBrowser: latest.browser ?? '',
      lastOs: latest.os ?? '',
      lastGeoCountry: latest.geoCountry ?? '',
      topDimension: this.mostFrequentDimension(visits),
      tags: Array.from(new Set(visits.flatMap((v) => v.tags ?? []))),
      latestIdentifier: sorted.find((v) => v.identifier)?.identifier,
      name: sorted.find((v) => v.name)?.name,
      email: sorted.find((v) => v.email)?.email,
    };
  }

  /** Most frequently seen "type:value" dimension composite across the visitor's visits. */
  private mostFrequentDimension(visits: DataPlatformVisitRow[]): string | undefined {
    const counts = new Map<string, number>();
    for (const visit of visits) {
      for (const dimension of visit.topDimensions ?? []) {
        if (dimension) counts.set(dimension, (counts.get(dimension) ?? 0) + 1);
      }
    }
    let top: string | undefined;
    let max = 0;
    for (const [dimension, count] of counts) {
      if (count > max) {
        max = count;
        top = dimension;
      }
    }
    return top;
  }

  private loadUrlInterestProfileMap() {
    this.urlInterestProfilesService
      .getUrlInterestProfileMap()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((map) => {
        this.urlInterestProfileMap.set(map);
        // Re-calculate computed properties with updated mapping
        if (this.profile) {
          this.topDimensionDisplay = this.formatTopDimension(this.profile.topDimension);
        }
      });
  }

  ngOnDestroy() {
    this.headerObserver?.disconnect();
    this.profileSubscription?.unsubscribe();
    this.visitsSubscription?.unsubscribe();
    this.purchasesSubscription?.unsubscribe();
    this.traitsSubscription?.unsubscribe();
    this.consentSubscription?.unsubscribe();
    this.insightsSubscription?.unsubscribe();
    this.visitStatusSubscription?.unsubscribe();
  }

  /**
   * Erases this person. Keyed by uid because the Profiles list holds uids and lists people with no
   * contact row. The gateway fans the delete out to both stores.
   */
  protected confirmDeleteProfile(): void {
    if (this.isDeleting) {
      return;
    }
    this.confirmationDialogService
      .confirm({
        title: 'data-platform.delete-profile',
        text: 'data-platform.delete-profile-confirm',
        proceedButtonText: 'data-platform.delete-confirm-action',
        cancelButtonText: 'generic.cancel',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed === true) {
          this.deleteProfile();
        }
      });
  }

  private deleteProfile(): void {
    this.isDeleting = true;
    this.dataPlatformService
      .deleteProfile(this.profile.uid, 'profile-flyout')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.isDeleting = false;
        if (result?.isPositive()) {
          this.snackBarService.success('data-platform.delete-profile-done');
          this.flyoutService.dataPlatformProfilesChangedBS.next(this.profile.uid);
          this.closeFlyout();
        } else {
          this.snackBarService.error('data-platform.delete-profile-failed');
        }
      });
  }

  /** Afterwards the flyout shows the profile the dialog closes with. */
  protected openUnmerge(): void {
    this.dialog
      .open<ProfileUnmergeDialogComponent, ProfileUnmergeDialogData, number>(ProfileUnmergeDialogComponent, {
        width: '640px',
        panelClass: 'profile-unmerge-dialog-panel',
        autoFocus: 'first-heading',
        disableClose: true,
        data: { uid: this.profile.uid },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((uid) => {
        if (uid == null) {
          return;
        }
        this.flyoutService.dataPlatformProfilesChangedBS.next(this.profile.uid);
        this.flyoutService.open({
          type: FlyoutDataType.DataPlatformProfile,
          uid,
          dataPlatformFilter: this.flyoutOpeningData?.dataPlatformFilter,
        } as FlyoutOpeningData);
      });
  }

  protected closeFlyout() {
    this.flyoutService.close();
  }

  /** Copies a value (identifier, coupon code) to the clipboard with a confirmation snackbar. */
  protected copyValue(value: string) {
    navigator.clipboard
      ?.writeText(value)
      .then(() => this.snackBarService.success('generic.copyToClipboard.copied'))
      .catch(() => {});
  }

  /** Opens the visit detail flyout for the exact visit (same view as the Visits page). */
  protected openVisit(summary: VisitSummary) {
    this.flyoutService.open({
      type: FlyoutDataType.DataPlatformVisit,
      uid: summary.visitRow.uid,
      dataPlatformVisitRow: summary.visitRow,
      dataPlatformFilter: this.flyoutOpeningData?.dataPlatformFilter,
      dataPlatformBackTitle: this.headerTitle,
      dataPlatformVisitSiblings: this.visitSummaries().map((s) => s.visitRow),
    } as FlyoutOpeningData);
  }

  /** Reopens the visit flyout this profile was opened from. */
  protected openBackVisit() {
    const row = this.flyoutOpeningData?.dataPlatformBackVisitRow;
    if (!row) {
      return;
    }
    this.flyoutService.open({
      type: FlyoutDataType.DataPlatformVisit,
      uid: row.uid,
      dataPlatformVisitRow: row,
      dataPlatformFilter: this.flyoutOpeningData?.dataPlatformFilter,
      dataPlatformBackTitle: this.headerTitle,
      dataPlatformVisitSiblings: this.visitSummaries().map((s) => s.visitRow),
    } as FlyoutOpeningData);
  }

  /**
   * Formats a top dimension for display, translating URL patterns to names.
   */
  private formatTopDimension(topDimension: string | undefined): string {
    if (!topDimension) return '';
    const map = this.urlInterestProfileMap();
    const colonIdx = topDimension.indexOf(':');
    if (colonIdx === -1) {
      return getDimensionTypeLabel(topDimension, this.translateService);
    }
    const type = topDimension.substring(0, colonIdx);
    const value = topDimension.substring(colonIdx + 1);

    return translateDimensionTypeValue(type, value, map, this.translateService);
  }

  private loadProperties() {
    this.traitsSubscription?.unsubscribe();
    this.traitsSubscription = this.dataPlatformService
      .getProperties({ uid: this.profile.uid }, 'profiles-flyout-properties')
      .subscribe({
        next: (result) => {
          if (result?.isPositive() && result.data) {
            this.rawTraits.set(Object.entries(result.data.properties).map(([key, value]) => ({ key, value })));
          }
        },
      });
  }

  private loadConsentEvents() {
    this.consentSubscription?.unsubscribe();
    this.consentSubscription = this.dataPlatformService
      .getConsentEvents({ uid: this.profile.uid }, 'profiles-flyout-consent')
      .subscribe({
        next: (result) => {
          if (result?.isPositive() && result.data) {
            this.consentEvents = result.data.events;
          }
        },
      });
  }

  private loadInsights() {
    const siteIds = this.flyoutOpeningData?.dataPlatformFilter?.siteIds;
    if (!siteIds?.length) return;

    const ctx = this.currentContextService.currentContextBS.getValue();
    const purchaseName = ctx?.account?.metricsGoalNames?.[0] || 'purchase';
    const presets = getInsightPresets(purchaseName);
    const presetIds = new Set(presets.map((p) => p.id));

    this.insightsSubscription?.unsubscribe();
    this.insightsSubscription = this.audienceService
      .getAllAudiences()
      .pipe(
        switchMap((audiences) => {
          // Saved audiences evaluate can read exactly, except any whose id matches a preset
          const savedMeta: { id: string; label: string }[] = [];
          const savedDefs: DataPlatformAudienceDefinition[] = [];
          for (const a of audiences) {
            if (!a.id || !a.name || a.isDefault || presetIds.has(a.id)) {
              continue;
            }
            const rules = audienceEvaluateRules(a);
            if (!rules) {
              continue;
            }
            savedMeta.push({ id: a.id, label: a.name });
            savedDefs.push({ id: a.id, rules });
          }

          const allDefs: DataPlatformAudienceDefinition[] = [
            ...presets.map((p) => ({ id: p.id, rules: p.rules })),
            ...savedDefs,
          ];

          return this.dataPlatformService
            .evaluateAudiences({ siteIds, uid: this.profile.uid, audiences: allDefs }, 'profiles-flyout-insights')
            .pipe(map((result) => ({ result, savedMeta })));
        }),
      )
      .subscribe({
        next: ({ result, savedMeta }) => {
          if (result?.isPositive() && result.data) {
            const matchedIds = new Set(result.data.matches.filter((m) => m.matched).map((m) => m.audienceId));
            const matchedPresets = presets.filter((p) => matchedIds.has(p.id));
            const matchedSaved: InsightPreset[] = savedMeta
              .filter((d) => matchedIds.has(d.id))
              .map((d) => ({ id: d.id, label: d.label, icon: 'bookmark', variant: 'neutral' as const, rules: [] }));
            this.matchedInsights.set([...matchedPresets, ...matchedSaved]);
          }
        },
      });
  }

  private loadDetails() {
    const siteIds = this.flyoutOpeningData?.dataPlatformFilter?.siteIds;
    if (!siteIds?.length) return;

    this.dataPlatformService
      .getProfileDetails({ canonicalUid: this.profile.uid, siteIds }, 'profiles-flyout-details')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (!result?.isPositive() || !result.data) return;
          const data = result.data;
          const fetched = (data.devices ?? []).map((d) => ({
            uid: d.uid,
            lastSeenDate: this.dateService.utcToLocalTime(d.lastSeen),
            browser: d.browser ?? '',
            os: d.os ?? '',
          }));
          // Anonymous profiles have no id_map row — fall back to the canonical_uid itself, reusing
          // the profile's last browser/os so the merged section still shows the latest device info.
          this.deviceViews =
            fetched.length > 0
              ? fetched
              : [
                  {
                    uid: this.profile.uid,
                    lastSeenDate: this.lastSeenDate,
                    browser: this.profile.lastBrowser ?? '',
                    os: this.profile.lastOs ?? '',
                  },
                ];
          this.rawIdentifiers.set(data.identifierDetails ?? []);
          this.source = data.source ?? null;
        },
      });
  }

  private loadVisits(keepRows = false) {
    const siteIds = this.flyoutOpeningData?.dataPlatformFilter?.siteIds;
    if (!siteIds?.length) return;

    this.visitsSubscription?.unsubscribe();
    if (!keepRows) {
      this.isLoadingVisits.set(true);
      this.visitSummaries.set([]);
    }

    this.visitsSubscription = this.dataPlatformService
      .getVisits(
        {
          filter: { siteIds, from: ALL_TIME_FROM },
          uid: this.profile.uid,
          includeEvents: false,
          includeTotalCount: false,
          includeDistributedCoupons: true,
        },
        'profiles-flyout-visits',
      )
      .subscribe({
        next: (result) => {
          this.isLoadingVisits.set(false);
          if (result?.isPositive() && result.data) {
            this.visitSummaries.set((result.data.visits || []).map((v) => this.toVisitSummary(v)));
            this.pollLatestVisit(result.data.visits || []);
          }
        },
        error: () => {
          this.isLoadingVisits.set(false);
        },
      });
  }

  private pollLatestVisit(visits: DataPlatformVisitRow[]) {
    const latest = visits.reduce<DataPlatformVisitRow | null>(
      (newest, visit) => (!newest || parseUtc(visit.endedAt) > parseUtc(newest.endedAt) ? visit : newest),
      null,
    );
    if (!latest || latest.sessionId === this.polledSessionId()) {
      return;
    }
    this.stopLatestVisitPolling();
    this.polledSessionId.set(latest.sessionId);
    this.visitStatusSubscription = pollVisitStatus(
      (request) => this.dataPlatformService.getVisitStatus(request, 'profiles-flyout-status'),
      latest,
    ).subscribe((status) => {
      const previous = this.latestVisitStatus();
      this.latestVisitStatus.set(status);
      if (previous && status && hasVisitStatusChanged(previous, status)) {
        this.loadVisits(true);
      }
    });
  }

  private stopLatestVisitPolling() {
    this.visitStatusSubscription?.unsubscribe();
    this.polledSessionId.set(null);
    this.latestVisitStatus.set(null);
  }

  private loadPurchases() {
    const siteIds = this.flyoutOpeningData?.dataPlatformFilter?.siteIds;
    if (!siteIds?.length) return;

    this.purchasesSubscription?.unsubscribe();
    this.isLoadingPurchases.set(true);
    this.purchaseVisits.set([]);

    this.purchasesSubscription = this.dataPlatformService
      .getVisits(
        {
          filter: { siteIds, from: ALL_TIME_FROM },
          uid: this.profile.uid,
          eventType: 'purchase',
          includeEvents: false,
          includeTotalCount: false,
          pageSize: 200,
          sortDirection: 'desc',
        },
        'profiles-flyout-purchases',
      )
      .subscribe({
        next: (result) => {
          this.isLoadingPurchases.set(false);
          if (result?.isPositive() && result.data) {
            this.purchaseVisits.set((result.data.visits || []).map((v) => this.toVisitSummary(v)));
          }
        },
        error: () => {
          this.isLoadingPurchases.set(false);
        },
      });
  }

  private toVisitSummary(visit: DataPlatformVisitRow): VisitSummary {
    const score = visit.engagementScore ?? 0;
    return {
      sessionId: visit.sessionId,
      startedAtDate: this.dateService.utcToLocalTime(visit.startedAt),
      pageCount: visit.pageViewCount ?? 0,
      goalCount: visit.goalCount ?? 0,
      revenue: visit.totalRevenue ?? 0,
      duration: formatVisitDuration(visit.sessionDuration),
      tier: getTier(score),
      tierTooltip: `${this.translateService.instant('data-platform.engagement-score')}: ${score}`,
      visitRow: visit,
    };
  }

  /** Opens the visit behind a purchase row (each purchase item is one visit). */
  protected openPurchaseVisit(item: PurchaseListItem) {
    const summary = this.purchaseVisits().find((purchase) => purchase.sessionId === item.key);
    if (summary) {
      this.openVisit(summary);
    }
  }
}
